import { useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import AdxButton from 'components/custom/AdxButton'

const AdxFormModal = ({
  title,
  subtitle,
  icon,
  iconColor = 'alternate.main',
  modalWidth = 750,
  handleClose,
  handleAction,
  customHeader,
  closeIcon = 'close_rounded',
  headerBorder = true,
  children,
  enableFooter = false,
  footerBorder = true,
  cancelText = 'Cancel',
  actionText = 'Action',
  actionButtonDisabled = false,
  isLoading,
  footerprops, // for custom footer
  sxModal,
  childPadding = true,
  pageState,
  sxChildren,
  disableAutoFocus = true,
  enableEnterSubmit = true,
  responsiveScroll = false,
  ...rest
}) => {
  const theme = useTheme()
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('md'))

  const handleKeyDown = (e) => {
    if (!enableEnterSubmit) return
    if (e.key !== 'Enter') return
    const tag = e.target?.tagName
    if (tag !== 'INPUT' && tag !== 'TEXTAREA') return
    if (tag === 'TEXTAREA') return
    e.preventDefault()
    handleAction?.()
  }


  const useResponsiveScroll = responsiveScroll && isCompactViewport

  const style = {
    position: 'absolute',
    borderRadius: '10px',
    boxShadow: 3,
    maxHeight: useResponsiveScroll ? 'calc(100dvh - 24px)' : '90vh',
    minWidth: useResponsiveScroll ? 'auto' : { xs: 1, md: '600px' },
    ...(useResponsiveScroll
      ? {
          top: 12,
          left: 12,
          right: 12,
          width: 'auto',
        }
      : {
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }),
    ...sxModal,
  }

  const contentRef = useRef(null)

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = 0
    }
  }, [pageState])

  return (
    <Modal
      {...rest}
      sx={
        useResponsiveScroll
          ? {
              '&.MuiModal-root': {
                overflowY: 'auto',
              },
            }
          : undefined
      }
    >
      <Box
        ref={contentRef}
        bgcolor="white.main"
        sx={style}
        boxSizing={'border-box'}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        minHeight={0}
        overflow={'hidden'}
        onKeyDown={handleKeyDown}
      >
        <Box
          sx={{
            borderBottom: headerBorder ? '1px solid' : 'none',
            borderBottomColor: 'gray-line.main',
            position: 'sticky',
            top: '0',
            background: 'white',
            zIndex: 100,
            borderRadius: '10px 10px 0 0',
          }}
        >
          {customHeader ? (
            customHeader
          ) : (
            <CustomHeader
              icon={icon}
              iconColor={iconColor}
              title={title}
              subtitle={subtitle}
              handleClose={handleClose}
              closeIcon={closeIcon}
            />
          )}
        </Box>
        <Box
          width={1}
          boxSizing="border-box"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            minHeight: 0,
            px: childPadding ? 3 : 0,
            py: childPadding ? 2 : 0,
            overflow: 'auto',
            ...sxChildren,
          }}
          className="scrollbar"
        >
          {children}
        </Box>
        {enableFooter && (
          <Box
            sx={{
              borderTop: footerBorder ? '1px solid' : 'none',
              borderColor: 'gray-line.main',
              position: 'sticky',
              background: 'white',
              zIndex: 100,
              borderRadius: '0 0 10px 10px',
            }}
          >
            {footerprops ? (
              footerprops
            ) : (
              <CustomFooter
                cancelText={cancelText}
                actionText={actionText}
                handleClose={handleClose}
                handleAction={handleAction}
                actionButtonDisabled={actionButtonDisabled}
                isLoading={isLoading}
              />
            )}
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default AdxFormModal

const CustomHeader = ({ icon, iconColor, title, subtitle, handleClose, closeIcon }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 3,
        py: 2,
        borderRadius: '10px 10px 0 0',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        {icon && (
          <Box
            sx={{
              borderRadius: '50%',
              color: iconColor,
              bgcolor: 'alternate.light',
              aspectRatio: 1,
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        )}
        <Box>
          <Box>
            <Typography
              variant="h4"
              fontWeight="bold"
              color="dark-text.main"
              sx={{ textTransform: 'capitalize' }}
            >
              {title}
            </Typography>
            <Typography variant="body1" color="light-gray-text.main">
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box onClick={handleClose} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <Icon sx={{ color: 'dark-text.main' }}>{closeIcon}</Icon>
      </Box>
    </Box>
  )
}

const CustomFooter = ({
  cancelText,
  actionText,
  handleClose,
  handleAction,
  actionButtonDisabled,
  isLoading,
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', md: 'row' },
        alignItems: 'center',
        justifyContent: { xs: 'center', md: 'end' },
        gap: 1,
        px: 3,
        py: 2,
        zIndex: 100,
        bgcolor: 'primary.light',
        borderColor: 'grayscale.line-light',
      }}
    >
      <AdxButton
        variant={'outlined'}
        sx={{ minWidth: '100px', width: { xs: 1, md: 'fit-content' } }}
        onClick={handleClose}
      >
        {cancelText}
      </AdxButton>
      <AdxButton
        sx={{ minWidth: '200px', width: { xs: 1, md: 'fit-content' } }}
        onClick={isLoading ? undefined : handleAction}
        disabled={actionButtonDisabled}
        isLoading={isLoading}
      >
        {actionText}
      </AdxButton>
    </Box>
  )
}
